/**
 * Phrases types d’observation — types partagés ; données chargées depuis GoPaaS (liste 718).
 */

export type ObservationPoste = 'GARDIEN' | 'DEFENSEUR' | 'MILIEU' | 'ATTAQUANT';

export type CommentTemplateCategory = 'strength' | 'improvement' | 'follow_up' | 'general';

export interface CommentTemplate {
  id: string;
  text: string;
  category: CommentTemplateCategory;
  poste: string;
  /** Phrase scout / analyse (mode avancé) — champ GoPaaS `pro` = 1. */
  pro: boolean;
  /** Affiché en tête du sheet (5 premières du poste filtré). */
  frequent: boolean;
  sortOrder?: number;
}

export const ADVANCED_COMMENT_TEMPLATES_STORAGE_KEY = 'tifosi_advanced_comment_templates';

export const COMMENT_TEMPLATE_CATEGORY_LABELS: Record<CommentTemplateCategory | 'all', string> = {
  all: 'Toutes',
  strength: 'Points forts',
  improvement: 'À travailler',
  follow_up: 'Suivi',
  general: 'Général',
};

export const COMMENT_TEMPLATE_CATEGORIES: readonly (CommentTemplateCategory | 'all')[] = [
  'all',
  'strength',
  'improvement',
  'follow_up',
  'general',
];

const FREQUENT_COUNT = 5;

const VALID_POSTES: readonly string[] = ['GARDIEN', 'DEFENSEUR', 'MILIEU', 'ATTAQUANT'];

/** Mappe le champ `type` GoPaaS vers la catégorie UI. */
export function mapCommentTemplateTypeFromApi(raw: string | undefined): CommentTemplateCategory {
  if (!raw?.trim()) return 'general';
  const t = raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
  if (t.includes('points forts') || t === 'strength') return 'strength';
  if (t.includes('travailler') || t === 'improvement') return 'improvement';
  if (t.includes('suivi') || t === 'follow_up' || t === 'follow up') return 'follow_up';
  if (t.includes('general') || t === 'general') return 'general';
  return 'general';
}

export function normalizeCommentTemplatePoste(raw: string | undefined): string {
  const key = (raw ?? '').trim().toUpperCase();
  if (!key) return 'GENERIQUE';
  if (key === 'GENERIC') return 'GENERIQUE';
  return key;
}

export function isCommentTemplateUsed(comments: string, templateText: string): boolean {
  const normalized = templateText.trim();
  if (!normalized) return false;
  return comments
    .split('\n')
    .map((line) => line.replace(/^\s*-\s*/, '').trim())
    .some((line) => line === normalized);
}

export function parseCommentTemplatePro(raw: unknown): boolean {
  if (raw === true || raw === 1) return true;
  if (raw === false || raw === 0) return false;
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  return s === '1' || s === 'true' || s === 'oui' || s === 'yes';
}

export function loadAdvancedCommentTemplatesPreference(): boolean {
  try {
    return localStorage.getItem(ADVANCED_COMMENT_TEMPLATES_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveAdvancedCommentTemplatesPreference(enabled: boolean): void {
  try {
    localStorage.setItem(ADVANCED_COMMENT_TEMPLATES_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Filtre la liste API par poste ; mode standard = pro 0 uniquement, avancé = tout. */
export function filterCommentTemplatesForPoste(
  templates: readonly CommentTemplate[],
  posteRaw: string,
  advancedMode = false,
): CommentTemplate[] {
  const playerPoste = posteRaw.trim().toUpperCase();
  const targetPoste = VALID_POSTES.includes(playerPoste) ? playerPoste : 'GENERIQUE';

  const filtered = templates
    .filter((t) => (advancedMode ? true : !t.pro))
    .filter((t) => normalizeCommentTemplatePoste(t.poste) === targetPoste)
    .sort((a, b) => {
      const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return a.text.localeCompare(b.text, 'fr');
    });

  return filtered.map((t, i) => ({
    ...t,
    frequent: i < FREQUENT_COUNT,
  }));
}
